import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

/**
 * The PatronEZ product mark — a sonar/radar sweep with blips that
 * surface in time with the rotating beam. Each blip carries a real
 * signal kind + timestamp. The whole composition is one SVG so it
 * scales and tree-shakes cleanly.
 *
 * Visual language:
 *  - 4 concentric range rings (50/130/210/290 px from center)
 *  - One rotating sweep arm (12s/rev) with a trailing wedge gradient
 *  - 5 blips: 4 at fixed bearings, 1 marquee-walking from far→near
 *    to imply "fresh signal entering the field"
 *  - Each blip has a small label that fades in when the sweep
 *    passes it (key-framed via animate-pulse-subtle staggered delays)
 */
export function SignalRadar() {
  const { t } = useTranslation();

  // Blips: angle in degrees from 12-o'clock, range (0..1 of outer ring),
  // i18n key for the label, kind tint.
  const blips = [
    { id: 'jc',  angle: 32,  range: 0.62, kindKey: 'radar.kind_job_change',     tint: '#F59E0B' }, // amber
    { id: 'fd',  angle: 95,  range: 0.45, kindKey: 'radar.kind_funding',         tint: '#10B981' }, // emerald
    { id: 'td',  angle: 165, range: 0.78, kindKey: 'radar.kind_tdnet',           tint: '#E11D48' }, // rose
    { id: 'hr',  angle: 232, range: 0.55, kindKey: 'radar.kind_hiring',          tint: '#0A5BFF' }, // accent
    { id: 'tch', angle: 305, range: 0.40, kindKey: 'radar.kind_tech',            tint: '#A855F7' }, // violet
  ];

  return (
    <div className="relative aspect-square w-full max-w-[520px] mx-auto select-none">
      <svg
        viewBox="-340 -340 680 680"
        className="absolute inset-0 h-full w-full"
        role="img"
        aria-label={t('radar.aria')}
      >
        <defs>
          {/* Sweep wedge — bright at the leading edge, fading to nothing
              behind. The whole <g> rotates; the wedge sits inside. */}
          <linearGradient id="sweepGrad" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="290" y2="0">
            <stop offset="0%"  stopColor="#0A5BFF" stopOpacity="0.55" />
            <stop offset="60%" stopColor="#0A5BFF" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#0A5BFF" stopOpacity="0" />
          </linearGradient>
          {/* Outer-glow filter for the leading edge */}
          <filter id="leadGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" />
          </filter>
        </defs>

        {/* Range rings */}
        {[80, 160, 240, 300].map((r, i) => (
          <circle
            key={r}
            cx="0"
            cy="0"
            r={r}
            fill="none"
            stroke="#0A5BFF"
            strokeOpacity={0.18 - i * 0.025}
            strokeWidth={i === 3 ? 1.4 : 1}
            strokeDasharray={i === 3 ? '0' : '3 5'}
          />
        ))}

        {/* Cross-hairs */}
        <g stroke="#0A5BFF" strokeOpacity="0.16" strokeWidth="1">
          <line x1="-300" y1="0" x2="300" y2="0" strokeDasharray="2 6" />
          <line x1="0" y1="-300" x2="0" y2="300" strokeDasharray="2 6" />
        </g>

        {/* Range labels — instrument feel */}
        <g
          fontFamily="'JetBrains Mono', ui-monospace, monospace"
          fontSize="9"
          fill="#0A5BFF"
          fillOpacity="0.55"
          letterSpacing="0.05em"
        >
          <text x="2" y="-78">0d</text>
          <text x="2" y="-158">7d</text>
          <text x="2" y="-238">30d</text>
          <text x="2" y="-298">90d</text>
        </g>

        {/* Compass labels NSEW (Tokyo, NYC, London, Singapore — the four
            regions we cover, mapped to compass points) */}
        <g
          fontFamily="'JetBrains Mono', ui-monospace, monospace"
          fontSize="10"
          fill="#0E1116"
          fillOpacity="0.55"
          fontWeight="600"
          letterSpacing="0.12em"
        >
          <text x="0" y="-310" textAnchor="middle">JP</text>
          <text x="312" y="4" textAnchor="start">NY</text>
          <text x="0" y="320" textAnchor="middle">SG</text>
          <text x="-312" y="4" textAnchor="end">UK</text>
        </g>

        {/* Sweep arm — rotates once every 12s */}
        <motion.g
          animate={{ rotate: 360 }}
          transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
          style={{ transformOrigin: '0px 0px' }}
        >
          {/* Wedge fill */}
          <path
            d="M 0,0 L 290,-44 A 293,293 0 0 1 290,44 Z"
            fill="url(#sweepGrad)"
          />
          {/* Leading bright edge */}
          <line
            x1="0"
            y1="0"
            x2="290"
            y2="0"
            stroke="#0A5BFF"
            strokeWidth="1.4"
            strokeOpacity="0.85"
            filter="url(#leadGlow)"
          />
          <circle cx="290" cy="0" r="3" fill="#0A5BFF" />
        </motion.g>

        {/* Blips (static — the sweep passes over them) */}
        {blips.map((b, i) => {
          const r = b.range * 290;
          const a = ((b.angle - 90) * Math.PI) / 180;
          const x = r * Math.cos(a);
          const y = r * Math.sin(a);
          return (
            <motion.g
              key={b.id}
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: 'easeOut',
                delay: (b.angle / 360) * 12,
                times: [0, 0.05, 1],
              }}
            >
              <circle cx={x} cy={y} r="10" fill={b.tint} fillOpacity="0.12" />
              <circle cx={x} cy={y} r="5" fill={b.tint} fillOpacity="0.45" />
              <circle cx={x} cy={y} r="2.2" fill={b.tint} />
            </motion.g>
          );
        })}

        {/* Center origin (own workspace) */}
        <circle cx="0" cy="0" r="6" fill="#0E1116" />
        <circle cx="0" cy="0" r="14" fill="none" stroke="#0E1116" strokeOpacity="0.35" />
      </svg>

      {/* Floating kind chips placed roughly where each blip sits */}
      {blips.map((b) => {
        const r = b.range * 290;
        const a = ((b.angle - 90) * Math.PI) / 180;
        // px from center, in SVG units, then mapped to % of container
        const xPct = 50 + (r * Math.cos(a)) / 6.8;
        const yPct = 50 + (r * Math.sin(a)) / 6.8;
        return (
          <motion.div
            key={`label-${b.id}`}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.6,
              delay: 0.4 + (b.angle / 360) * 1.2,
            }}
            className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            style={{ left: `${xPct}%`, top: `${yPct}%` }}
          >
            <span
              className="num inline-block text-[10px] tracking-[0.12em] uppercase font-semibold px-2 py-0.5 rounded-md bg-white shadow-soft border border-ink-200"
              style={{ color: b.tint }}
            >
              {t(b.kindKey)}
            </span>
          </motion.div>
        );
      })}

      {/* Legend at bottom — clarifies what the user is looking at */}
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-3 text-[10px] uppercase tracking-[0.16em] text-ink-500 font-medium whitespace-nowrap">
        <span className="num">{t('radar.legend_origin')}</span>
        <span className="h-px w-8 bg-ink-300" />
        <span>{t('radar.legend_signals')}</span>
      </div>
    </div>
  );
}
